import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Link, Navigate, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { manifestByOpeningId } from "../../content/manifest.ts";
import { summaryBySlug } from "../../content/lookup.ts";
import { emptyReviewMessage, REVIEW_GRADE_LINE } from "../../trainer/practiceCopy.ts";
import { getTrainer, parseTrainMode, type TrainMode } from "../../trainer/session.ts";
import {
  loadBoardOverlays,
  saveBoardOverlay,
  DEFAULT_BOARD_OVERLAYS,
  type BoardOverlay,
} from "../../trainer/boardOverlays.ts";
import { CoachPanel } from "./CoachPanel.tsx";
import { MovesPanel } from "./SidePanels.tsx";
import { SessionRecapModal } from "./SessionRecap.tsx";
import { ReportCardModal } from "./ReportCardModal.tsx";
import { TrainerBoard } from "./TrainerBoard.tsx";
import { Scorecard } from "./Scorecard.tsx";
import { GuideNotes, OverlayControls } from "./OverlayControls.tsx";
import { replySquares } from "../../trainer/replyBriefing.ts";
import { useTrainer } from "./useTrainer.ts";
import { usePageMeta } from "../seo.ts";
import { loadedOpeningIntro } from "../../content/catalogCopy.ts";
import { keepSiteOffline } from "../../offline.ts";
import { SWITCH_PARAM, decodeMoves, encodeMoves } from "../../trainer/courseSwitchParam.ts";
import { practiceDisclosure } from "../../practice/disclosure.ts";
import { courseDeparture } from "../../trainer/courseRecovery.ts";

// The book trail is its own small chunk: the trainer's first paint does not wait for it.
const BookTrail = lazy(() => import("./BookTrail.tsx"));
const PracticeTools = lazy(() =>
  import("./PracticePanel.tsx").then((module) => ({ default: module.PracticeTools }))
);
const PracticeFeedbackPanel = lazy(() =>
  import("./PracticePanel.tsx").then((module) => ({ default: module.PracticeFeedbackPanel }))
);

function subscribeCompactPractice(listener: () => void) {
  const query = matchMedia("(max-width: 900px)");
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}
const isCompactPractice = () => matchMedia("(max-width: 900px)").matches;

export function TrainerPage() {
  const compactPractice = useSyncExternalStore(
    subscribeCompactPractice,
    isCompactPractice,
    () => false
  );
  const navigate = useNavigate();
  const { courseId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const course = summaryBySlug(courseId);
  const snapshot = useTrainer();
  const [startAttempt, setStartAttempt] = useState(0);
  const [courseLoad, setCourseLoad] = useState<{
    slug: string;
    attempt: number;
    status: "ready" | "error";
    invalidSwitch?: boolean;
    /** The session the load produced; a live course switch keeps it. */
    sessionId?: string;
  } | null>(null);
  // The course whose failed load the player retried: only that retry starts fresh.
  const retryFor = useRef<string | null>(null);
  const [dismissedRecapSession, setDismissedRecapSession] = useState<string | null>(null);
  const recapDismissed = dismissedRecapSession === snapshot.sessionId;
  const [axesExpanded, setAxesExpanded] = useState(false);
  const [boardOverlays, setBoardOverlays] = useState(loadBoardOverlays);
  // The reply card's squares go on the board while the pointer rests on the
  // card, or for good once "Show on board" is pressed.
  const [replyPinned, setReplyPinned] = useState(false);
  const [replyHovered, setReplyHovered] = useState(false);
  const [reviewingCard, setReviewingCard] = useState<{ sessionId: string; id: string } | null>(
    null
  );
  const reviewedCard =
    reviewingCard?.sessionId === snapshot.sessionId
      ? snapshot.reportCards.find((card) => card.id === reviewingCard.id) || null
      : null;

  const urlMode: TrainMode = parseTrainMode(params.get("mode"));
  const switchParam = params.get(SWITCH_PARAM);
  const mode: TrainMode = snapshot.slug === course?.slug ? snapshot.mode : urlMode;
  // The card's squares are rung only where the card itself is shown.
  const replyBriefing = (mode === "learn" && snapshot.opponentNote?.briefing) || null;
  const replyHighlight =
    replyBriefing && (replyPinned || replyHovered) ? replySquares(replyBriefing) : null;

  useEffect(() => {
    if (!course) return;
    let active = true;
    const slug = course.slug;
    // A switch from another course carries its moves over: this course
    // starts from that position instead of resuming or starting fresh.
    const carried = decodeMoves(switchParam);
    // Returning to a game in progress (say, from Progress or a Theory page)
    // picks it up where it was; a retry after a failed load starts fresh.
    let invalidSwitch = false;
    void (async () => {
      if (switchParam !== null) {
        const { isCourseSwitchPosition } = await import("../../trainer/courseSwitch.ts");
        if (!carried.length || !isCourseSwitchPosition(course.openingId, carried)) {
          invalidSwitch = true;
          throw new Error("Invalid course link");
        }
      }
      if (!active) return;
      await getTrainer().start({
        slug,
        mode: params.has("mode") ? urlMode : undefined,
        resume: !carried.length && !(startAttempt > 0 && retryFor.current === slug),
        ...(carried.length ? { prefixSans: carried } : {}),
        offerCourseSwitches: true,
        mix: params.get("mix") !== "0" && urlMode === "review",
      });
    })()
      .then(() => {
        keepSiteOffline();
        if (active)
          setCourseLoad({
            slug,
            attempt: startAttempt,
            status: "ready",
            sessionId: getTrainer().getSnapshot().sessionId,
          });
        // Legacy position links retain their checkpoint. Live course changes
        // preserve the complete game in the session instead of using this URL.
      })
      .catch(() => {
        if (active) setCourseLoad({ slug, attempt: startAttempt, status: "error", invalidSwitch });
      });
    return () => {
      active = false;
      // Leaving before a Black course has downloaded: don't let it replace the
      // game in progress when it arrives.
      getTrainer().cancelPendingStart();
    };
    // Mode toggles after start must not reset the position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.slug, startAttempt, switchParam]);

  // A restart from the Practice menu starts a new session in this course; it
  // is still this page's game, so a live course switch from it keeps the board.
  if (
    courseLoad?.status === "ready" &&
    courseLoad.slug === course?.slug &&
    snapshot.slug === course.slug &&
    courseLoad.sessionId !== snapshot.sessionId
  )
    setCourseLoad({ ...courseLoad, sessionId: snapshot.sessionId });

  usePageMeta({
    title: course ? `${course.title} trainer — The First 15 Moves` : "Trainer — The First 15 Moves",
    description: course
      ? `Train the first 15 moves of the ${course.title}.`
      : "Train the first 15 moves of a classic opening.",
    path: `/train/${courseId}`,
  });

  if (!course) return <Navigate to="/openings" replace />;
  // Downloaded with the course, so it is ready whenever the board is.
  const intro = loadedOpeningIntro(course.openingId);

  // Black courses load on demand. Never display or accept moves on the old
  // course while a different route's content is downloading.
  // A live course switch moves the session to the new course; the URL follows
  // a moment later (navigation is a transition). The game this page loaded
  // goes on, so the board stays up instead of flashing the loading screen:
  // while the URL still names the course this page loaded, and once it names
  // the session's new course. Any other route change still waits for its load.
  // The page is keyed by course, so it mounts fresh when the URL arrives: a
  // game this course already holds is shown at once, since its start only
  // resumes it.
  const liveSwitch =
    (courseLoad?.status === "ready" &&
      courseLoad.sessionId === snapshot.sessionId &&
      snapshot.courseJourney.length > 1 &&
      (snapshot.slug === course.slug || courseLoad.slug === course.slug)) ||
    (courseLoad === null &&
      startAttempt === 0 &&
      switchParam === null &&
      snapshot.courseJourney.length > 1 &&
      getTrainer().canResume(course.slug, params.has("mode") ? urlMode : undefined));
  if (
    !liveSwitch &&
    (courseLoad?.slug !== course.slug ||
      courseLoad.attempt !== startAttempt ||
      courseLoad.status !== "ready" ||
      snapshot.slug !== course.slug)
  ) {
    const failed =
      courseLoad?.slug === course.slug &&
      courseLoad.attempt === startAttempt &&
      courseLoad.status === "error";
    return (
      <section className="panel openings-panel" aria-label={`${course.title} course`}>
        <h2>{course.title}</h2>
        <p role={failed ? "alert" : "status"}>
          {failed && courseLoad.invalidSwitch
            ? "This opening link does not contain a valid position for this course. Your existing game has not been changed."
            : failed
              ? "We couldn’t load this course. Check your connection and try again."
              : "Loading the lessons and preparing your board…"}
        </p>
        {failed ? (
          <button
            className="btn-primary btn-sm"
            type="button"
            onClick={() => {
              if (courseLoad.invalidSwitch) setParams({ mode: urlMode }, { replace: true });
              retryFor.current = course.slug;
              setStartAttempt((attempt) => attempt + 1);
            }}
          >
            {courseLoad.invalidSwitch ? "Start this course from move one" : "Try again"}
          </button>
        ) : null}{" "}
        <Link className="btn-ghost btn-sm" to="/openings">
          Browse openings
        </Link>
      </section>
    );
  }

  const activeSlug = manifestByOpeningId(snapshot.openingId)?.slug || course.slug;
  const startRecall = () => {
    if (activeSlug !== course.slug) {
      void navigate(`/train/${activeSlug}?mode=recall`, { replace: true });
      return;
    }
    setParams({ mode: "recall" }, { replace: true });
    void getTrainer().startRecall();
  };
  const startBranch = () => {
    if (activeSlug !== course.slug) {
      void navigate(`/train/${activeSlug}?mode=branches`, { replace: true });
      return;
    }
    setParams({ mode: "branches" }, { replace: true });
    void getTrainer().startBranch();
  };
  const setMode = (next: TrainMode) => {
    setParams({ mode: next }, { replace: true });
    if (snapshot.mode === "review" && activeSlug !== course.slug) {
      void navigate(`/train/${activeSlug}?mode=${next}`, { replace: true });
      return;
    }
    void getTrainer().start({ slug: course.slug, mode: next, mix: next === "review" });
  };

  const reviewEmpty =
    mode === "review" &&
    snapshot.slug === course.slug &&
    snapshot.reviewRemaining === 0 &&
    !snapshot.practiceFeedback &&
    !snapshot.sessionEnded;
  const announcement = snapshot.liveAnnouncement;
  const disclosure = practiceDisclosure(mode, Boolean(snapshot.practiceFeedback));

  const clearBoardOverlays = () => {
    setBoardOverlays((current) => {
      for (const key of Object.keys(current) as BoardOverlay[])
        if (current[key]) saveBoardOverlay(key, false);
      return { ...DEFAULT_BOARD_OVERLAYS };
    });
  };

  const toggleBoardOverlay = (key: BoardOverlay) => {
    setBoardOverlays((current) => {
      const enabled = !current[key];
      saveBoardOverlay(key, enabled);
      return { ...current, [key]: enabled };
    });
  };

  return (
    <section
      id="landing"
      className={`panel hero trainer-hero${mode !== "learn" ? " is-practice" : ""}${snapshot.moveNumber === 0 && mode !== "review" ? " is-starting" : ""}`}
      aria-label="Play the opening"
    >
      <div className="sr-only" id="live-region" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      <div className="hero-board" id="hero-board">
        <div className="hero-board-header">
          <div className="hero-board-meta">
            <span className="now-learning-label" aria-hidden="true">
              Now learning
            </span>
            <div className="hero-board-title-row">
              <h2 className="opening-title" id="openingTitle">
                {snapshot.title || course.title}
              </h2>
              <span className="side-chip">
                Playing {snapshot.playerSide === "b" ? "Black" : "White"}
              </span>
              <span className="eco-chip" id="openingEco">
                {snapshot.eco || course.eco}
              </span>
              {(snapshot.limitedCoverage || course.limitedCoverage) && (
                <span className="coverage-chip" id="openingCoverage">
                  Limited book coverage
                </span>
              )}
              <span className="coverage-chip">
                {course.reviewStatus === "draft" ? "Draft" : "Reviewed"}
              </span>
            </div>
          </div>
          <div className="hero-board-header-right">
            <div className="phase-chip" id="phaseChip">
              <span className="phase-chip-move">
                <span id="moveCounter">{snapshot.moveNumber}</span>
                <span className="phase-chip-total"> / {snapshot.freePlay ? 40 : 15}</span>
              </span>
            </div>
          </div>
        </div>

        {compactPractice &&
        mode !== "learn" &&
        !snapshot.practiceFeedback &&
        !reviewEmpty &&
        !snapshot.sessionEnded ? (
          <section className="practice-mobile-question" aria-label="Your decision">
            {snapshot.branchIntro ? <p className="ym-eyebrow">{snapshot.branchIntro}</p> : null}
            <p>{snapshot.inputLocked ? "Reviewing your decision…" : snapshot.objective}</p>
            {snapshot.hints.slice(0, snapshot.hintRevealed).map((hint) => (
              <p key={hint.id}>
                <strong>{hint.label}.</strong> {hint.text}
              </p>
            ))}
          </section>
        ) : null}
        {mode !== "learn" ? (
          <Suspense fallback={null}>
            <PracticeTools snapshot={snapshot} />
          </Suspense>
        ) : null}
        {snapshot.practiceSaveError ? (
          <div className="practice-save-error" role="alert">
            <p>{snapshot.practiceSaveError}</p>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => void getTrainer().retryPracticeSave()}
            >
              Try saving again
            </button>
          </div>
        ) : null}
        {snapshot.practiceAlternatives.length ? (
          <aside className="practice-alternative-offer" aria-label="Study a different route">
            <p>
              Your move has a lesson of its own. Keep recalling this stretch, or study that route:
            </p>
            {snapshot.practiceAlternatives.map((target) => (
              <Link
                key={target.slug}
                className="btn-ghost btn-sm"
                to={`/train/${target.slug}?mode=learn&${SWITCH_PARAM}=${encodeURIComponent(encodeMoves(target.moves))}`}
              >
                Study {target.title}
              </Link>
            ))}
          </aside>
        ) : null}
        <div className="board-toolbar" aria-label="Session actions">
          {reviewEmpty ? null : (
            <OverlayControls
              value={boardOverlays}
              snapshot={snapshot}
              onToggle={toggleBoardOverlay}
              onClear={clearBoardOverlays}
            />
          )}
          <div className="board-actions">
            <button
              className="btn-primary btn-sm"
              id="btnHint"
              type="button"
              onClick={() => getTrainer().revealHint()}
              disabled={snapshot.inputLocked || snapshot.hintRevealed >= 5}
              aria-disabled={snapshot.hintRevealed >= 5}
            >
              Hint
            </button>
            <button
              className="btn-ghost btn-sm"
              id="btnUndo"
              type="button"
              disabled={!snapshot.canUndo}
              onClick={() => getTrainer().undo()}
            >
              Take back
            </button>
            {snapshot.reportCards.length > 0 && !snapshot.reportCard ? (
              <button
                className="btn-ghost btn-sm"
                type="button"
                onClick={() =>
                  setReviewingCard({
                    sessionId: snapshot.sessionId,
                    id: snapshot.reportCards[snapshot.reportCards.length - 1].id,
                  })
                }
              >
                Report card
              </button>
            ) : null}
            {snapshot.recap && recapDismissed ? (
              <button
                className="btn-ghost btn-sm"
                type="button"
                onClick={() => setDismissedRecapSession(null)}
              >
                Session recap
              </button>
            ) : null}
            {mode !== "learn" &&
            mode !== "review" &&
            !snapshot.practiceFeedback &&
            !snapshot.sessionEnded ? (
              <button
                type="button"
                className="btn-ghost btn-sm practice-show-example"
                disabled={snapshot.inputLocked}
                onClick={() => getTrainer().revealPracticeAnswer()}
              >
                Show an example
              </button>
            ) : null}
            {mode === "review" && !reviewEmpty && !snapshot.practiceFeedback ? (
              <>
                <p className="practice-grade-line">{REVIEW_GRADE_LINE}</p>
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  disabled={snapshot.inputLocked}
                  onClick={() => void getTrainer().gradeReview(false)}
                >
                  Show the move
                </button>
              </>
            ) : null}
          </div>
        </div>

        {reviewEmpty ? (
          <div className="practice-empty" role="status">
            <p>{emptyReviewMessage(snapshot.reviewSoon, snapshot.nextReviewAt)}</p>
            {snapshot.reviewSavedCount ? (
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() =>
                  void getTrainer().start({
                    slug: course.slug,
                    mode: "review",
                    mix: true,
                    practiceScope: { recent: true },
                  })
                }
              >
                Practise recent positions
              </button>
            ) : null}
            <button type="button" className="btn-ghost btn-sm" onClick={startRecall}>
              Recall a short line
            </button>
            <button type="button" className="btn-ghost btn-sm" onClick={() => setMode("learn")}>
              Play the main line
            </button>
          </div>
        ) : (
          <>
            <TrainerBoard
              snapshot={snapshot}
              overlays={boardOverlays}
              replySquares={replyHighlight}
              onAnnounce={(text) => getTrainer().announce(text)}
            />
            {compactPractice && snapshot.practiceFeedback ? (
              <Suspense fallback={<p>Preparing your feedback…</p>}>
                <PracticeFeedbackPanel key={snapshot.practiceFeedback.taskId} snapshot={snapshot} />
              </Suspense>
            ) : null}
            <Suspense fallback={null}>
              <BookTrail snapshot={snapshot} />
            </Suspense>
            <GuideNotes value={boardOverlays} snapshot={snapshot} />
          </>
        )}

        {reviewEmpty ? null : (
          <Scorecard
            history={snapshot.history}
            mode={mode}
            review={{
              total: snapshot.reviewTotal,
              remaining: snapshot.reviewRemaining,
              result: snapshot.reviewResult,
            }}
            practice={snapshot.practice}
            memory={
              mode === "recall"
                ? { found: snapshot.memoryFound, asked: snapshot.memoryAsked }
                : undefined
            }
          />
        )}

        {snapshot.pendingFinish && !snapshot.reportCard && !snapshot.sessionEnded ? (
          <div className="decide-banner" role="status">
            <p>
              Fifteen moves complete. Keep playing into the middlegame, or finish with the recap?
            </p>
            <button
              className="btn-primary btn-sm"
              type="button"
              onClick={() => getTrainer().acknowledgeReportCard("continue")}
            >
              Keep playing
            </button>
            <button
              className="btn-ghost btn-sm"
              type="button"
              onClick={() => getTrainer().acknowledgeReportCard("finish")}
            >
              Finish
            </button>
          </div>
        ) : null}
      </div>

      <div className="trainer-side">
        {snapshot.practiceFeedback ? (
          compactPractice ? null : (
            <Suspense fallback={<p>Preparing your feedback…</p>}>
              <PracticeFeedbackPanel key={snapshot.practiceFeedback.taskId} snapshot={snapshot} />
            </Suspense>
          )
        ) : (
          <CoachPanel
            coach={mode === "learn" ? snapshot.coach : null}
            inputLocked={snapshot.inputLocked}
            playerSide={snapshot.playerSide}
            intro={
              intro &&
              snapshot.moveNumber === 0 &&
              mode !== "recall" &&
              mode !== "review" &&
              mode !== "branches" &&
              !snapshot.sessionEnded &&
              (!intro.opponentFirst || snapshot.history[0]?.san === intro.opponentFirst)
                ? intro
                : null
            }
            nextBook={snapshot.sessionEnded ? null : snapshot.nextBook}
            moveNumber={snapshot.moveNumber}
            objective={snapshot.objective}
            moveOptions={mode === "learn" ? snapshot.moveOptions : []}
            phaseLabel={snapshot.phaseLabel}
            mode={mode}
            hints={snapshot.hints}
            hintRevealed={snapshot.hintRevealed}
            opponentNote={disclosure.futureMoves ? snapshot.opponentNote : null}
            axesExpanded={axesExpanded}
            onToggleAxes={() => setAxesExpanded((v) => !v)}
            onHint={() => getTrainer().revealHint()}
            positionReading={mode === "learn" ? snapshot.positionReading : null}
            themes={snapshot.themes}
            onMainline={snapshot.onMainline}
            courseRecovery={
              mode === "learn" &&
              !snapshot.nextBook &&
              !snapshot.onMainline &&
              !snapshot.freePlay &&
              !snapshot.inputLocked &&
              !snapshot.sessionEnded
                ? courseDeparture(snapshot.history, snapshot.routeLessons)
                : null
            }
            courseTitle={snapshot.title}
            onReturnToCourse={() => getTrainer().returnToCourse()}
            branchIntro={snapshot.branchIntro}
            branchChoices={snapshot.branchChoices}
            onChooseBranch={(san) => getTrainer().chooseBranch(san)}
            replyShownOnBoard={replyPinned}
            onShowReplyOnBoard={setReplyPinned}
            onReplyHover={setReplyHovered}
          />
        )}
        <MovesPanel history={snapshot.history} />
      </div>

      {snapshot.reportCard ? (
        <ReportCardModal
          key={snapshot.reportCard.id}
          card={snapshot.reportCard}
          mode="live"
          onContinue={() => getTrainer().acknowledgeReportCard("continue")}
          onFinish={() => getTrainer().acknowledgeReportCard("finish")}
          onLater={() => getTrainer().acknowledgeReportCard("later")}
          onClose={() => getTrainer().acknowledgeReportCard("later")}
        />
      ) : reviewedCard ? (
        <ReportCardModal
          key={`review-${reviewedCard.id}`}
          card={reviewedCard}
          mode="review"
          onContinue={() => setReviewingCard(null)}
          onFinish={() => setReviewingCard(null)}
          onLater={() => setReviewingCard(null)}
          onClose={() => setReviewingCard(null)}
        />
      ) : null}

      {snapshot.recap && !recapDismissed && !snapshot.reportCard && !reviewedCard ? (
        <SessionRecapModal
          recap={snapshot.recap}
          title={snapshot.title}
          finalPlan={snapshot.recap.middlegamePlan ?? undefined}
          onClose={() => setDismissedRecapSession(snapshot.sessionId)}
          onRepeatLine={() => void getTrainer().start({ slug: course.slug, mode })}
          onRecall={startRecall}
          onBranch={snapshot.practiceBranches.length ? startBranch : undefined}
          onRepeatWeak={() => {
            const prefixSans = getTrainer().repeatWeakPrefix();
            setParams({ mode: "recall" }, { replace: true });
            void getTrainer().start({
              slug: course.slug,
              mode: "recall",
              prefixSans,
            });
          }}
        />
      ) : null}
    </section>
  );
}
